import Box from "@mui/material/Box";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import ClearIcon from "@mui/icons-material/Clear";
import Collapse from "@mui/material/Collapse";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import LinearProgress from "@mui/material/LinearProgress";
import Grid from "@mui/material/Grid";
import { Typography } from "@mui/material";
import SpreadsheetCardContent from "./SpreadsheetCardContent";
import SpreadsheetCardSettings from "./SpreadsheetCardSettings";
import {
  defaultSheetFile,
  reduxSheetFile,
  preFormatJson,
} from "./spreadsheetutils";
import { openSharedDataFile, fetchSharedDataFile } from "./sharedDatasetApi";
import {
  actionSheetFileRemoved,
  actionSheetFileUpdated,
  selectorSheet,
} from "./spreadsheetRedux";
import { actionMapZoomFeaturesUpdated } from "../Map";
import { actionAppBarFloorUpdated } from "../AppBar";

const card_ = (theme) => ({
  minWidth: 275,
  marginBottom: "8px",
  "&:hover": {
    backgroundColor: "rgba(217, 217, 217, 0.2)",
  },
});

const cardError_ = (theme) => ({
  border: "1px solid #d32f2f",
});

const buttonError_ = (theme) => ({
  color: "#d32f2f",
});

const expand_ = (theme) => ({
  transform: "rotate(0deg)",
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shortest,
  }),
});

const expandOpen_ = (theme) => ({
  transform: "rotate(180deg)",
});

const hr_ = (theme) => ({
  backgroundColor: "lightgrey",
  height: 1,
  border: 0,
});

const rightIcon_ = (theme) => ({
  marginLeft: "auto",
});

const checked_ = (theme) => ({
  "& .Mui-checked": {
    color: "var(--fill-color)",
  },
});

const track_ = (theme) => ({
  backgroundColor: "var(--fill-color)",
});

const headerActions_ = (theme) => ({
  display: "flex",
});

const headerTitle_ = (theme) => ({
  flexGrow: 2,
});

const row_ = (theme) => ({
  lineHeight: 1,
  // override card actions margin issue
  "&:not(:first-of-type)": {
    marginLeft: "0px",
  },
  marginLeft: "0px",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    color: "black",
  },
  // https://mui.com/material-ui/migration/upgrade-to-grid-v2/#container-width
  width: "100%",
  alignItems: "center",
  justifyContent: "center",
});

const gridContent_ = (theme) => ({
  alignSelf: "center",
  display: "flex",
  justifyContent: "center",
  "& .MuiTypography-overline": {
    lineHeight: "24px",
    height: 24,
    fontSize: "0.7rem",
  },
  "& .MuiButton-root": {
    fontSize: "0.7rem",
  },
});

const accordionBtn_ = (theme) => ({
  padding: 0,
  minWidth: 24,
  minHeight: 24,
  "&:hover": {
    textDecoration: "underline",
  },
});

const floors_ = (theme) => ({
  maxHeight: 115,
  padding: "0px",
  borderTop: "1px solid rgba(0,0,0,0.1)",
  borderBottom: "1px solid rgba(0,0,0,0.1)",
  overflowY: "scroll",
  "&::-webkit-scrollbar": {
    webkitAppearance: "none",
    width: 7,
    backgroundColor: "rgba(0, 0, 0, 0.1)",
  },
  "&::-webkit-scrollbar-thumb": {
    borderRadius: "2px",
    backgroundColor: "rgba(0, 0, 0, .5)",
    webkitBoxShadow: "0 0 1px rgba(255, 255, 255, .5)",
  },
});

const floorActions_ = (theme) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
});

const linearProgress_ = (theme) => ({
  width: "100%",
  height: "24px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
});

export default function SpreadsheetCard({ sheetId }) {
  const [expanded, setExpanded] = useState(false);
  const dispatch = useDispatch();
  const idToken = useSelector((state) => state.authReducer.idToken);

  const {
    layersAllVisible,
    fillColor,
    lineColor,
    fileName,
    bbox,
    buildingData,
    status,
    rmrecnbr,
    lnglat,
    sharedDataId,
  } = useSelector(selectorSheet(sheetId));
  const { fillColor: defaultFill, lineColor: defaultLine } =
    defaultSheetFile(sheetId);

  const { sharedDataFilesArray } = useSelector(
    (state) => state.spreadsheetReducer,
  );

  const layerFloors = buildingData
    ? buildingData.map((data) => data.data.floor)
    : [];
  const rrnError = bbox === null || rmrecnbr === null;
  const lnglatError =
    bbox === null ||
    lnglat === null ||
    lnglat.length !== 2 ||
    !Number.isInteger(lnglat[0]) ||
    !Number.isInteger(lnglat[1]);
  const canZoom =
    layerFloors.length > 0 &&
    Array.isArray(bbox) &&
    bbox[0] !== null &&
    bbox[1] !== null;
  const invalidHeaders = rrnError && lnglatError && status !== "loading";

  // set custom CSS variables
  const customStyle = {
    "--fill-color": fillColor || defaultFill,
    "--line-color": lineColor || defaultLine,
  };

  const handleOpenFile = () => {
    const sharedDataFile = sharedDataFilesArray.find(
      (file) => file.id === sharedDataId,
    );
    const mimeType = sharedDataFile?.mimeType;
    openSharedDataFile(sharedDataId, mimeType);
  };

  const handleLayerVisibilityToggled = (event) => {
    dispatch(
      actionSheetFileUpdated({
        id: sheetId,
        layersAllVisible: event.target.checked,
      }),
    );
  };

  const handleLayerRemoval = () => {
    dispatch(actionSheetFileRemoved({ id: sheetId }));
  };

  const handleRefreshData = async () => {
    try {
      dispatch(
        actionSheetFileUpdated({
          id: sheetId,
          fileName: fileName,
          status: "loading",
          json: [[], []],
          lnglat: null,
          rmrecnbr: null,
          type: null,
        }),
      );

      const sheetData = defaultSheetFile(sheetId);
      const reduxData = reduxSheetFile();
      const { data: rawJson } = await fetchSharedDataFile(
        sharedDataId,
        idToken,
      );
      const { headerRowIndex, rmrecnbr, lnglat, type, json, preHeaderJson } =
        preFormatJson(rawJson);

      dispatch(
        actionSheetFileUpdated({
          ...sheetData,
          id: sheetId,
          fileName: fileName,
          rmrecnbr: rmrecnbr,
          lnglat: lnglat,
          json: json,
          headerRowIndex: headerRowIndex,
          preHeaderJson: preHeaderJson,
          type: type,
          defaultText: rmrecnbr,
          status: "complete",
          ...reduxData,
        }),
      );
    } catch (e) {
      console.error("Failed to refresh file content: " + e.message);
      dispatch(
        actionSheetFileUpdated({
          id: sheetId,
          status: "error",
        }),
      );
    }
  };

  const handleErrorClick = () => {
    dispatch(
      actionSheetFileUpdated({
        id: sheetId,
        contentExpanded: true,
        contentRmrecnbrExpanded: true,
        contentSheetDataExpanded: false,
        contentBuildingDataExpanded: false,
        contentSelectedRoomExpanded: false,
      }),
    );
    if (!expanded) setExpanded(true);
  };

  return (
    <Card
      variant="outlined"
      sx={[card_, invalidHeaders && cardError_]}
      style={customStyle}
    >
      <CardActions sx={headerActions_} disableSpacing>
        <Tooltip describeChild title="Toggle Visibility">
          <Switch
            sx={checked_}
            slotProps={{
              input: {
                "aria-label": "toggle layer visibility on map",
              },
              track: {
                sx: track_,
              },
            }}
            color="default"
            checked={layersAllVisible}
            onChange={handleLayerVisibilityToggled}
            name="checkedA"
          />
        </Tooltip>
        <Typography noWrap={true} sx={headerTitle_}>
          {fileName}
        </Typography>
        {sharedDataId && (
          <>
            <Tooltip title="Open in Google Drive">
              <IconButton
                size="small"
                aria-label="Open file in Google Drive"
                onClick={handleOpenFile}
              >
                <OpenInNewIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh Data">
              <IconButton
                size="small"
                aria-label="Refresh spreadsheet data"
                onClick={handleRefreshData}
                disabled={status === "loading"}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </>
        )}
        <Tooltip title="show more options">
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            sx={[expand_, rightIcon_, expanded && expandOpen_]}
            aria-expanded={expanded}
            aria-label="show more options"
          >
            <ExpandMoreIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Remove File">
          <IconButton
            size="small"
            aria-label="remove spreadsheet file"
            onClick={handleLayerRemoval}
          >
            <ClearIcon />
          </IconButton>
        </Tooltip>
      </CardActions>
      <Box sx={floors_}>
        <CardActions sx={floorActions_}>
          {invalidHeaders ? (
            <Button
              sx={buttonError_}
              variant="outlined"
              onClick={handleErrorClick}
            >
              Select Column Headers
            </Button>
          ) : (
            <BuildingDisplay buildingData={buildingData} canZoom={canZoom} />
          )}
        </CardActions>
      </Box>
      <Collapse in={expanded}>
        <CardContent>
          <SpreadsheetCardContent sheetId={sheetId} />
          <Box component="hr" sx={hr_} />
          <SpreadsheetCardSettings sheetId={sheetId} />
        </CardContent>
      </Collapse>
    </Card>
  );
}

function BuildingDisplay({ canZoom, buildingData }) {
  const dispatch = useDispatch();

  const buildingObj = buildingSort(buildingData);

  const handleZoom = (bbox, floor) => {
    dispatch(
      actionMapZoomFeaturesUpdated({
        type: "bbox",
        data: bbox,
      }),
    );
    if (floor) {
      dispatch(actionAppBarFloorUpdated(floor));
    }
  };

  if (!canZoom || !buildingObj) {
    return (
      <Box sx={linearProgress_}>
        <LinearProgress color="primary" />
      </Box>
    );
  }

  return Object.keys(buildingObj)
    .sort()
    .map((building) => (
      <Grid key={building} container sx={row_}>
        <Grid size={{ xs: 4 }} sx={gridContent_}>
          <Typography variant="overline">{building}</Typography>
        </Grid>
        <Grid size={{ xs: 8 }} sx={gridContent_}>
          {buildingObj[building].map(({ bbox, floor }) => {
            const zoomFloor = floor === "No Floor" ? null : floor;
            return (
              <Button
                size="small"
                color="primary"
                sx={accordionBtn_}
                onClick={() => handleZoom(bbox, zoomFloor)}
                key={floor || "No Floor"}
              >
                {floor || "No Floor"}
              </Button>
            );
          })}
        </Grid>
      </Grid>
    ));
}

function buildingSort(buildingData) {
  if (!buildingData) return;
  const returnObj = {};

  buildingData.forEach(({ data, bbox }) => {
    if (!returnObj[data.building]) {
      returnObj[data.building] = [
        {
          floor: data.floor,
          bbox,
        },
      ];
    } else {
      returnObj[data.building].push({
        floor: data.floor,
        bbox,
      });
    }
  });

  return returnObj;
}
